"use client";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  IconButton,
  CardActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { PlayArrow } from "@mui/icons-material";
import { LikeIcon, ChatBubbleIcon } from "../../assets/reactIcons";
import { Icons } from "../../assets/icon";

interface PostCardProps {
  mediaType?: string;
  mediaUrl?: string;
  title: string;
  description: string;
  likes?: number;
  comments?: number;
  timestamp?: string;
  onLike?: () => void;
  onComment?: () => void;
  onShare?: () => void;
}

const PostCard = ({
  mediaType = "none",
  mediaUrl = "",
  title,
  description,
  likes = 0,
  comments = 0,
  timestamp = "",
  onLike,
  onComment,
  onShare,
}: PostCardProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Card sx={{ 
      padding: isMobile ? 1 : 2, 
      border: "1px solid var(--neutral-100)", 
      backgroundColor: "var(--neutral-50)", 
      borderRadius: "16px" 
    }}>
      {mediaType !== "none" && (
        <Box sx={{ position: "relative" }}>
          {mediaType === "video" ? (
            <video
              controls
              src={mediaUrl}
              title={title}
              style={{ width: "100%", borderRadius: "4px" }}
            />
          ) : (
            <CardMedia
              component="img"
              image={mediaUrl}
              title={title}
              sx={{ 
                borderRadius: "4px", 
                height: isMobile ? "200px" : "300px", 
                objectFit: "cover" 
              }}
            />
          )}
          {mediaType === "video" && (
            <IconButton
              aria-label="play"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                },
                padding: isMobile ? 1 : 2,
              }}
            >
              <PlayArrow sx={{ fontSize: isMobile ? 40 : 60 }} />
            </IconButton>
          )}
        </Box>
      )}

      <CardContent sx={{ padding: isMobile ? "8px 0" : "16px 0" }}>
        <Typography 
          sx={{ 
            fontSize: isMobile ? "12px" : "14px", 
            fontWeight: 400, 
            color: "var(--neutral-700)",
            lineHeight: 1.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {description}
        </Typography>
      </CardContent>

      <CardActions disableSpacing sx={{ padding: 0, width: "100%" }}>
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          gap: isMobile ? 1 : 2, 
          width: "100%",
          flexWrap: "wrap"
        }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton aria-label="like" size="small" onClick={onLike}>
              <LikeIcon style={{ fontSize: isMobile ? 16 : 20, color: "black" }} />
            </IconButton>
            <Typography
              sx={{
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                color: "rgba(0, 0, 0, 0.6)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {likes} likes
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton aria-label="comment" size="small" onClick={onComment}>
              <ChatBubbleIcon style={{ fontSize: isMobile ? 16 : 20, color: "black" }} />
            </IconButton>
            <Typography
              sx={{
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                color: "var(--neutral-1000)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {comments} Comments
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton aria-label="share" size="small" onClick={onShare}>
              <Icons.ShareIcon height={isMobile ? 16 : 20} width={isMobile ? 16 : 20} />
            </IconButton>
            <Typography
              sx={{
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                color: "rgba(0, 0, 0, 0.6)",
                display: "flex",
                alignItems: "center",
              }}
            >
              Share
            </Typography>
          </Box>

          {timestamp && (
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "flex-end", 
              flex: 1,
              marginLeft: isMobile ? 0 : "auto"
            }}>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontSize: isMobile ? "0.7rem" : "0.75rem" }}
              >
                {timestamp}
              </Typography>
            </Box>
          )}
        </Box>
      </CardActions>
    </Card>
  );
};

export default PostCard;
